import { createContext, useContext, useState, useEffect, useCallback, type ReactNode, type ComponentType } from 'react';

interface PageTitleState {
  title: string;
  icon?: ComponentType<{ className?: string }>;
}

interface PageTitleContextValue {
  pageTitle: PageTitleState;
  setPageTitle: (state: PageTitleState) => void;
}

const PageTitleContext = createContext<PageTitleContextValue | null>(null);

export function PageTitleProvider({ children }: { children: ReactNode }) {
  const [pageTitle, setPageTitleState] = useState<PageTitleState>({ title: '' });

  const setPageTitle = useCallback((state: PageTitleState) => {
    setPageTitleState(state);
  }, []);

  return (
    <PageTitleContext.Provider value={{ pageTitle, setPageTitle }}>
      {children}
    </PageTitleContext.Provider>
  );
}

export function usePageTitle() {
  const ctx = useContext(PageTitleContext);
  if (!ctx) throw new Error('usePageTitle must be used within PageTitleProvider');
  return ctx;
}

export function useSetPageTitle(title: string, icon?: ComponentType<{ className?: string }>) {
  const { setPageTitle } = usePageTitle();

  useEffect(() => {
    setPageTitle({ title, icon });
    return () => setPageTitle({ title: '' });
  }, [title, icon, setPageTitle]);
}
